import { cn } from "@design-system/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const badgeVariants = cva(
  "border-border-default focus-visible:ring-ring-default inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border px-2 py-0.5 text-xs leading-4 font-semibold whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px]",
  {
    variants: {
      semantic: {
        neutral: "",
        critical: "",
        warning: "",
        success: "",
        waiting: "",
        active: "",
      },
      emphasis: {
        emphasised: "border-transparent",
        default: "border-transparent",
        muted: "",
      },
    },
    compoundVariants: [
      // Neutral
      {
        semantic: "neutral",
        emphasis: "emphasised",
        className:
          "bg-shape-neutral-inverted-background text-shape-neutral-inverted-foreground-default [a&]:hover:bg-shape-neutral-inverted-background-hover [button&]:hover:bg-shape-neutral-inverted-background-hover",
      },
      {
        semantic: "neutral",
        emphasis: "default",
        className:
          "bg-shape-neutral-filled-background text-shape-neutral-filled-foreground-default [a&]:hover:bg-shape-neutral-filled-background-hover [button&]:hover:bg-shape-neutral-filled-background-hover",
      },
      {
        semantic: "neutral",
        emphasis: "muted",
        className:
          "bg-shape-neutral-outlined-background text-shape-neutral-outlined-foreground-default border-shape-neutral-outlined-border-default [a&]:hover:bg-shape-neutral-outlined-background-hover [button&]:hover:bg-shape-neutral-outlined-background-hover",
      },
      // Critical
      {
        semantic: "critical",
        emphasis: "emphasised",
        className:
          "bg-shape-critical-inverted-background text-shape-critical-inverted-foreground-default [a&]:hover:bg-shape-critical-inverted-background-hover [button&]:hover:bg-shape-critical-inverted-background-hover",
      },
      {
        semantic: "critical",
        emphasis: "default",
        className:
          "bg-shape-critical-filled-background text-shape-critical-filled-foreground-default [a&]:hover:bg-shape-critical-filled-background-hover [button&]:hover:bg-shape-critical-filled-background-hover",
      },
      {
        semantic: "critical",
        emphasis: "muted",
        className:
          "bg-shape-critical-outlined-background text-shape-critical-outlined-foreground-default border-shape-critical-outlined-border-default [a&]:hover:bg-shape-critical-outlined-background-hover [button&]:hover:bg-shape-critical-outlined-background-hover",
      },
      // Warning
      {
        semantic: "warning",
        emphasis: "emphasised",
        className:
          "bg-shape-warning-inverted-background text-shape-warning-inverted-foreground-default [a&]:hover:bg-shape-warning-inverted-background-hover [button&]:hover:bg-shape-warning-inverted-background-hover",
      },
      {
        semantic: "warning",
        emphasis: "default",
        className:
          "bg-shape-warning-filled-background text-shape-warning-filled-foreground-default [a&]:hover:bg-shape-warning-filled-background-hover [button&]:hover:bg-shape-warning-filled-background-hover",
      },
      {
        semantic: "warning",
        emphasis: "muted",
        className:
          "bg-shape-warning-outlined-background text-shape-warning-outlined-foreground-default border-shape-warning-outlined-border-default [a&]:hover:bg-shape-warning-outlined-background-hover [button&]:hover:bg-shape-warning-outlined-background-hover",
      },
      // Success
      {
        semantic: "success",
        emphasis: "emphasised",
        className:
          "bg-shape-success-inverted-background text-shape-success-inverted-foreground-default [a&]:hover:bg-shape-success-inverted-background-hover [button&]:hover:bg-shape-success-inverted-background-hover",
      },
      {
        semantic: "success",
        emphasis: "default",
        className:
          "bg-shape-success-filled-background text-shape-success-filled-foreground-default [a&]:hover:bg-shape-success-filled-background-hover [button&]:hover:bg-shape-success-filled-background-hover",
      },
      {
        semantic: "success",
        emphasis: "muted",
        className:
          "bg-shape-success-outlined-background text-shape-success-outlined-foreground-default border-shape-success-outlined-border-default [a&]:hover:bg-shape-success-outlined-background-hover [button&]:hover:bg-shape-success-outlined-background-hover",
      },
      // Waiting
      {
        semantic: "waiting",
        emphasis: "emphasised",
        className:
          "bg-shape-waiting-inverted-background text-shape-waiting-inverted-foreground-default [a&]:hover:bg-shape-waiting-inverted-background-hover [button&]:hover:bg-shape-waiting-inverted-background-hover",
      },
      {
        semantic: "waiting",
        emphasis: "default",
        className:
          "bg-shape-waiting-filled-background text-shape-waiting-filled-foreground-default [a&]:hover:bg-shape-waiting-filled-background-hover [button&]:hover:bg-shape-waiting-filled-background-hover",
      },
      {
        semantic: "waiting",
        emphasis: "muted",
        className:
          "bg-shape-waiting-outlined-background text-shape-waiting-outlined-foreground-default border-shape-waiting-outlined-border-default [a&]:hover:bg-shape-waiting-outlined-background-hover [button&]:hover:bg-shape-waiting-outlined-background-hover",
      },
      // Active
      {
        semantic: "active",
        emphasis: "emphasised",
        className:
          "bg-shape-active-inverted-background text-shape-active-inverted-foreground-default [a&]:hover:bg-shape-active-inverted-background-hover [button&]:hover:bg-shape-active-inverted-background-hover",
      },
      {
        semantic: "active",
        emphasis: "default",
        className:
          "bg-shape-active-filled-background text-shape-active-filled-foreground-default [a&]:hover:bg-shape-active-filled-background-hover [button&]:hover:bg-shape-active-filled-background-hover",
      },
      {
        semantic: "active",
        emphasis: "muted",
        className:
          "bg-shape-active-outlined-background text-shape-active-outlined-foreground-default border-shape-active-outlined-border-default [a&]:hover:bg-shape-active-outlined-background-hover [button&]:hover:bg-shape-active-outlined-background-hover",
      },
    ],
    defaultVariants: {
      semantic: "neutral",
      emphasis: "default",
    },
  }
);

export interface BadgeProps
  extends React.ComponentProps<"span">, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({
  className,
  semantic,
  emphasis,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ semantic, emphasis }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
